/**
 * CR315: Visibility Stats Breakdown Component
 * CR229: Added EXPIRED_KEPT bucket support for keep_expired mode
 * 
 * Displays the public visibility breakdown by category:
 * Active, Ongoing, Expired (in grace), Expired (kept), Hidden (expired beyond grace), Missing Dates
 */

import { Badge } from "@/components/ui/badge";
import { 
  Activity, 
  Clock, 
  Timer, 
  EyeOff, 
  AlertCircle,
  CheckCircle2,
  Archive,
} from "lucide-react";
import type { VisibilityStats, RetentionPolicyMode } from "@/lib/deals/publicVisibility";

interface VisibilityStatsBreakdownProps {
  stats: VisibilityStats | null;
  policy?: { mode: RetentionPolicyMode; grace_days: number } | null;
  compact?: boolean;
  className?: string;
}

export function VisibilityStatsBreakdown({ 
  stats, 
  policy,
  compact = false,
  className = "",
}: VisibilityStatsBreakdownProps) {
  if (!stats) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>
        No visibility data available
      </div>
    );
  }

  const isKeepExpiredMode = policy?.mode === "keep_expired";

  if (compact) {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/30">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Visible: {stats.visible_on_public}
        </Badge>
        <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/30">
          <Activity className="h-3 w-3 mr-1" />
          Active: {stats.active}
        </Badge>
        {stats.ongoing > 0 && (
          <Badge variant="outline" className="bg-purple-500/10 text-purple-700 border-purple-500/30">
            <Clock className="h-3 w-3 mr-1" />
            Ongoing: {stats.ongoing}
          </Badge>
        )}
        {stats.expired_grace > 0 && (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
            <Timer className="h-3 w-3 mr-1" />
            Grace: {stats.expired_grace}
          </Badge>
        )}
        {/* CR229: Show EXPIRED_KEPT bucket in keep_expired mode */}
        {stats.expired_kept > 0 && (
          <Badge variant="outline" className="bg-orange-500/10 text-orange-700 border-orange-500/30">
            <Archive className="h-3 w-3 mr-1" />
            Kept: {stats.expired_kept}
          </Badge>
        )}
        {stats.expired_hidden > 0 && (
          <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-500/30">
            <EyeOff className="h-3 w-3 mr-1" />
            Hidden: {stats.expired_hidden}
          </Badge>
        )}
        {stats.missing_dates > 0 && (
          <Badge variant="outline" className="bg-gray-500/10 text-gray-700 border-gray-500/30">
            <AlertCircle className="h-3 w-3 mr-1" />
            No Dates: {stats.missing_dates}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Summary */}
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium">Visible on Public</span>
        </div>
        <span className="text-lg font-bold text-green-600">
          {stats.visible_on_public}
          <span className="text-sm text-muted-foreground font-normal ml-1">
            / {stats.total_in_snapshot}
          </span>
        </span>
      </div>

      {/* Breakdown grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {/* Active */}
        <div className="p-2 bg-background rounded border">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <Activity className="h-3 w-3 text-blue-500" />
            Active
          </div>
          <div className="text-lg font-semibold text-blue-600">{stats.active}</div>
        </div>

        {/* Ongoing */}
        <div className="p-2 bg-background rounded border">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <Clock className="h-3 w-3 text-purple-500" />
            Ongoing
          </div>
          <div className="text-lg font-semibold text-purple-600">{stats.ongoing}</div>
        </div>

        {/* Expired in Grace */}
        <div className="p-2 bg-background rounded border">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <Timer className="h-3 w-3 text-amber-500" />
            Expired (Grace)
          </div>
          <div className="text-lg font-semibold text-amber-600">{stats.expired_grace}</div>
        </div>

        {/* CR229: Expired Kept - shown when keep_expired mode or count > 0 */}
        {(isKeepExpiredMode || stats.expired_kept > 0) && (
          <div className="p-2 bg-background rounded border">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Archive className="h-3 w-3 text-orange-500" />
              Expired (Kept)
            </div>
            <div className="text-lg font-semibold text-orange-600">
              {stats.expired_kept}
              {isKeepExpiredMode && (
                <span className="text-xs text-green-600 ml-1">(visible)</span>
              )}
            </div>
          </div>
        )}

        {/* Expired Hidden - only show in normal mode or if count > 0 */}
        {(!isKeepExpiredMode || stats.expired_hidden > 0) && (
          <div className="p-2 bg-background rounded border">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <EyeOff className="h-3 w-3 text-red-500" />
              Expired (Hidden)
            </div>
            <div className="text-lg font-semibold text-red-600">
              {stats.expired_hidden}
            </div>
          </div>
        )}

        {/* Missing Dates */}
        <div className="p-2 bg-background rounded border">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <AlertCircle className="h-3 w-3 text-gray-500" />
            Missing Dates
          </div>
          <div className="text-lg font-semibold text-gray-600">
            {stats.missing_dates}
            {isKeepExpiredMode && stats.missing_dates > 0 && (
              <span className="text-xs text-green-600 ml-1">(visible)</span>
            )}
          </div>
        </div>

        {/* Hidden by Decision / Hard Removed */}
        <div className="p-2 bg-background rounded border">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <EyeOff className="h-3 w-3 text-slate-500" />
            Suppressed
          </div>
          <div className="text-lg font-semibold text-slate-600">
            {stats.hidden_by_decision + stats.hard_removed}
            <span className="text-xs text-muted-foreground ml-1 font-normal">
              ({stats.hidden_by_decision} hidden, {stats.hard_removed} removed)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
